import { motion } from 'framer-motion';
import type { SegmentRule, VoterCounts, VoterDataFieldDto, SuggestionCount } from '@signalvote/shared';
import { VoterCount } from './VoterCount';
import { isNarrowed, valuesPhrase } from './voterSegment';

const MAX_SUGGESTIONS = 4;

/**
 * "Often added next": the server's suggested narrowings, each with the count the list would have once it's added,
 * taken from the last counts. A field narrowed since then drops out at once. While those counts are
 * an older list's, standing in until this one's land, the chips show no count, as the search does.
 */
export function VoterDataSuggestions({
  fields,
  rules,
  counts,
  stale,
  capReason,
  onAdd,
}: {
  fields: VoterDataFieldDto[];
  rules: SegmentRule[];
  counts: VoterCounts | undefined;
  stale: boolean;
  /** Why no rule can be added, at L2's filter cap. */
  capReason: string | null;
  onAdd: (field: VoterDataFieldDto, values: string[]) => void;
}) {
  const suggestions = (counts?.suggestions ?? [])
    .map((s) => ({ ...s, field: fields.find((f) => f.id === s.fieldId) }))
    .filter((s): s is SuggestionCount & { field: VoterDataFieldDto } => s.field !== undefined && !isNarrowed(s.field, rules))
    .slice(0, MAX_SUGGESTIONS);
  if (suggestions.length === 0) return null;

  return (
    <div role="group" aria-label="Often added next" className="mb-10 space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">Often added next</h3>
      {capReason && <p className="text-sm text-muted-foreground">{capReason}</p>}
      <div className="flex flex-wrap gap-2">
        {suggestions.map((s) => (
          <motion.button
            layout="position"
            key={s.fieldId}
            type="button"
            disabled={capReason !== null}
            onClick={() => onAdd(s.field, s.values)}
            className="inline-flex disabled:pointer-events-none disabled:opacity-50 min-h-9 items-center gap-2 rounded-full border border-dashed border-border bg-background px-3.5 text-sm font-semibold text-foreground/80 transition-[transform,color,border-color] hover:border-primary hover:text-primary active:scale-[0.97] motion-reduce:transition-none"
          >
            + {valuesPhrase(s.field, s.values)}
            {!stale && (
              <span className="text-xs font-semibold tabular-nums text-muted-foreground">
                <VoterCount value={s.count} />
              </span>
            )}
          </motion.button>
        ))}
      </div>
    </div>
  );
}
